import { BottomSheetHeight } from '@/components/bottom-sheet/constants'
import { ProcessingStatusList } from '@/uppy-file-upload/components/ProcessingStatusList'
import { useProcessingSummary } from '@/uppy-file-upload/hooks/useProcessingSummary'
import { BottomSheet } from '@/components/bottom-sheet/BottomSheet'
import { DropFilesToUploadLabel } from '@/uppy-file-upload/components/DropFilesToUploadLabel'
import { ProgressBar } from '@/components/progress-bar/ProgressBar'

type FileUploadDrawerProps = {
  /**
   * Whether the upload drawer is expanded.
   */
  expanded: boolean
  /**
   * Called when the drawer expanded state should change.
   */
  onExpandedChange: (expanded: boolean) => void
  /**
   * Called when the drawer close button is clicked.
   */
  onClose: () => void
  /**
   * Cancels an in-flight upload by its client temporary id.
   */
  onCancel: (id: string) => void
  /**
   * Removes a finished or failed item from the processing list (dismiss card).
   */
  onDismiss: (id: string) => void
  /**
   * Left offset applied on desktop to clear the side nav.
   * `true` clears the expanded nav, `'rail'` clears the 48px contracted rail,
   * and `false` spans the full viewport width for when no nav is showing.
   */
  offsetForSidebar: boolean | 'rail'
}

export function FileUploadDrawer({ expanded, onExpandedChange, onClose, onCancel, onDismiss, offsetForSidebar }: FileUploadDrawerProps) {
  const { status, readyCount, totalCount } = useProcessingSummary()

  if (totalCount === 0) {
    return (
      <BottomSheet
        expanded={false} // this can never be expanded because there are no files to show
        title={<DropFilesToUploadLabel />}
        expandedHeight={BottomSheetHeight.XL}
        offsetForSidebar={offsetForSidebar}
      />
    )
  }

  const getTitleText = () => {
    if (status === 'ready') return 'Files ready to use'
    if (status === 'failed') return 'Error with files'
    if (status === 'partial_failure') return 'Files ready with some issues'
    return `Preparing files (${readyCount} of ${totalCount})`
  }

  return (
    <BottomSheet
      expanded={expanded}
      title={getTitleText()}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      expandedHeight={BottomSheetHeight.XL}
      offsetForSidebar={offsetForSidebar}
      fixedContent={
        <div className="px-4">
          <ProgressBar value={status === 'processing' ? readyCount : 0} max={status === 'processing' ? totalCount : 0} minVisiblePercent={1} />
        </div>
      }
    >
      <div className="px-4 pb-4">
        <ProcessingStatusList includeQueued onCancel={onCancel} onDismiss={onDismiss} />
      </div>
    </BottomSheet>
  )
}
